// 缓冲区数据项类型
export interface BufferItem {
    code?: number
    command?: string
    content?: any
    index?: number
    indexChunk?: Array<number>
    sessionId?: string
    sessionMessageId?: string
    sessionLongId?: string
}

export interface BufferItems extends Array<BufferItem> {}

// 构造函数选项类型
interface TypewriterOptions {
    charactersPerSecond?: number
    charactersPerChunk?: number
    autoSpeedAdjust?: boolean
    speedThreshold?: number
    fastSpeedMultiplier?: number
    slowSpeedMultiplier?: number
}

// 显示回调函数类型
type DisplayCallback = (data: BufferItem | null | undefined, isComplete: boolean) => void

// 结束回调函数类型
type EndCallback = () => void

const SSE_CODE = {
    PENDING: 0, // Waiting for processing
    SUCCESS: 1, // Operation succeeded
    FAILURE: 2, // Operation failed
    DUPLICATE: 3 // Duplicate event
}

const SSE_COMMAND = {
    ID: 'id',
    SESSION_ID: 'sessionId',
    SESSION_MESSAGE_ID: 'sessionMessageId',
    SESSION_LONG_ID: 'sessionLongId',
    RETRY: 'retry',
    PENDING: 'pending',
    DONE: 'done',
    PART_START: 'part_start',
    PART_DONE: 'part_done',
    AGENT_START: 'agent_start',
    AGENT_END: 'agent_end'
}

export default class TypewriterBuffer {
    private buffer: BufferItem[]
    private timer: ReturnType<typeof setTimeout> | null
    private destroyed: boolean
    private charactersPerSecond: number
    private charactersPerChunk: number
    private displayCallback: DisplayCallback
    private endCallback: EndCallback
    private originalSpeed: number
    private autoSpeedAdjust: boolean
    private speedThreshold: number
    private fastSpeedMultiplier: number
    private slowSpeedMultiplier: number
    private lastLogTime: number

    constructor(options: TypewriterOptions = {}) {
        this.buffer = [] // 存储字符
        this.timer = null
        this.destroyed = false
        this.charactersPerSecond = options.charactersPerSecond || 60 // 默认每秒60字符
        this.charactersPerChunk = options.charactersPerChunk || 3 // 默认每次返回3个字符
        this.displayCallback = (_data: BufferItem | null | undefined, _isComplete: boolean) => {}
        this.endCallback = () => {}

        // 动态速度调整配置
        this.originalSpeed = this.charactersPerSecond // 保存原始速度
        this.autoSpeedAdjust = options.autoSpeedAdjust !== false // 默认启用自动速度调整
        this.speedThreshold = options.speedThreshold || 30 // 剩余文字阈值，超过此数量时加速
        this.fastSpeedMultiplier = options.fastSpeedMultiplier || 1.2 // 加速倍数
        this.slowSpeedMultiplier = options.slowSpeedMultiplier || 1.0 // 正常速度倍数
        this.lastLogTime = 0
    }

    setDisplayCallback(callback: DisplayCallback): void {
        this.displayCallback = callback
    }

    setEndCallback(callback: EndCallback): void {
        this.endCallback = callback
    }

    private log(p1: unknown, p2: unknown): void {
        if (Date.now() - this.lastLogTime > 2000) {
            console.error(p1, p2)
            this.lastLogTime = Date.now()
        }
    }

    // 添加文本到缓冲区（AI返回的数据）
    addData(originData: BufferItems): void {
        if (this.destroyed) {
            console.warn('打字机已销毁，无法添加文本')
            return
        }

        const data = originData.map(item => {
            let sessionId = ''
            let sessionMessageId = ''
            let sessionLongId = ''
            let command = item.command
            let content = ''

            switch (command) {
                case SSE_COMMAND.SESSION_ID:
                    sessionId = item.content.sessionId
                    break
                case SSE_COMMAND.SESSION_MESSAGE_ID:
                    sessionMessageId = item.content.sessionMessageId
                    break
                case SSE_COMMAND.SESSION_LONG_ID:
                    sessionLongId = item.content.sessionLongId
                    break
                case SSE_COMMAND.PENDING:
                    content = item.content.content
                    break
                case SSE_COMMAND.DONE:
                    content = '[DONE]'
                    break
            }

            return {
                code: item.code,
                index: item.index,
                command,
                content,
                sessionId,
                sessionMessageId,
                sessionLongId
            }
        })

        // console.log(
        //     1111,
        //     data.map(item => item.index)
        // )

        // 1. 对传入的 data 按 index 去重（保留第一个出现的）
        const uniqueData = this.deduplicateByIndex(data)

        // console.log(
        //     2222,
        //     uniqueData.map(item => item.index)
        // )

        // 2. 获取 buffer 中已存在的 index 集合
        const existingIndexSet = new Set(this.buffer.map(item => item.index))

        // 3. 过滤掉 buffer 中已存在的 index
        const newData = uniqueData.filter(item => !existingIndexSet.has(item.index))

        // console.log(
        //     3333,
        //     newData.map(item => item.index)
        // )

        // 4. 添加去重后的数据到 buffer
        this.buffer = this.buffer.concat(newData).sort((a, b) => (a.index || 0) - (b.index || 0))

        // console.log(
        //     4444,
        //     this.buffer.map(item => item.index)
        // )

        // 如果当前没有在处理，启动打字机效果
        if (!this.timer) {
            this.startTypewriter()
        }
    }

    // 按 index 去重（保留第一个出现的）
    private deduplicateByIndex(data: BufferItems): BufferItems {
        const seen = new Set<number>()
        const result: BufferItems = []

        for (const item of data) {
            if (!seen.has(item.index || 0)) {
                seen.add(item.index || 0)
                result.push(item)
            }
        }

        return result
    }

    // 启动打字机效果
    private startTypewriter(): void {
        this.startTimerTypewriter()
    }

    // 基于setTimeout的打字机（精确时间控制）
    private startTimerTypewriter(): void {
        if (this.destroyed || this.buffer.length === 0) {
            this.timer = null
            this.displayCallback(null, true) // 通知完成
            return
        }

        // 检查是否遇到结束标记
        const doneIndex = this.buffer.findIndex(item => item.content === '[DONE]')

        if (doneIndex === 0) {
            // 如果第一个就是结束标记，直接处理
            this.buffer.shift()
            this.timer = null
            this.endCallback() // 通知完成
            return
        }

        // 动态调整速度
        this.adjustSpeedByRemainingText()

        // 获取指定数量的字符
        const chunkSize = Math.min(this.charactersPerChunk, this.buffer.length)
        let chunk: Partial<BufferItem> = {
            indexChunk: [],
            content: '',
            sessionId: '',
            sessionMessageId: '',
            sessionLongId: ''
        }

        // 如果遇到[DONE]标记，只取到标记之前的字符
        const actualChunkSize = doneIndex > 0 ? Math.min(chunkSize, doneIndex) : chunkSize

        for (let i = 0; i < actualChunkSize; i++) {
            const shiftChunk: BufferItem = this.buffer.shift() || {
                indexChunk: [],
                content: '',
                sessionId: '',
                sessionMessageId: '',
                sessionLongId: ''
            }

            try {
                chunk = {
                    // @ts-ignore
                    indexChunk: (chunk.indexChunk || []).concat(shiftChunk.index || []),
                    content: (chunk.content || '') + (shiftChunk.content || ''),
                    sessionId: chunk.sessionId || shiftChunk.sessionId,
                    sessionMessageId: chunk.sessionMessageId || shiftChunk.sessionMessageId,
                    sessionLongId: chunk.sessionLongId || shiftChunk.sessionLongId
                }
            } catch (error) {
                chunk = {
                    content: (chunk.content || '') + (shiftChunk.content || ''),
                    sessionId: chunk.sessionId || shiftChunk.sessionId,
                    sessionMessageId: chunk.sessionMessageId || shiftChunk.sessionMessageId,
                    sessionLongId: chunk.sessionLongId || shiftChunk.sessionLongId
                }
            }
        }

        this.displayCallback(chunk as BufferItem, false)

        let interval = 1000 / this.charactersPerSecond

        interval = interval < 1 ? 0 : interval

        this.timer = setTimeout(() => {
            this.startTimerTypewriter()
        }, interval)
    }

    // 根据剩余文字数量动态调整速度
    private adjustSpeedByRemainingText(): void {
        if (!this.autoSpeedAdjust) return

        // 计算实际剩余文字数量（排除[DONE]标记）
        const doneIndex = this.buffer.findIndex(item => item.content === '[DONE]')
        const remainingTextLength = doneIndex > 0 ? doneIndex : this.buffer.length

        if (remainingTextLength > this.speedThreshold) {
            // 剩余文字较多，使用快速模式
            this.charactersPerSecond = this.originalSpeed * Math.ceil(remainingTextLength / this.speedThreshold) * this.fastSpeedMultiplier
        } else {
            // 剩余文字较少，恢复到正常速度
            this.charactersPerSecond = this.originalSpeed * this.slowSpeedMultiplier
        }
    }

    // 动态调整速度
    setSpeed(charactersPerSecond: number): void {
        if (charactersPerSecond <= 0) {
            console.warn('字符速度必须大于0')
            return
        }

        this.originalSpeed = charactersPerSecond // 更新原始速度
        this.charactersPerSecond = charactersPerSecond
        console.log(`打字机速度已调整为: ${charactersPerSecond}字符/秒`)
    }

    // 动态调整每次返回的字符数量
    setChunkSize(charactersPerChunk: number): void {
        if (charactersPerChunk <= 0) {
            console.warn('每次返回的字符数量必须大于0')
            return
        }

        this.charactersPerChunk = charactersPerChunk
        console.log(`打字机每次返回字符数已调整为: ${charactersPerChunk}个字符`)
    }

    // 立即显示所有剩余文本
    flushAll(): void {
        if (this.destroyed) return

        // 停止当前动画
        this.stopAnimation()

        // 显示所有剩余字符
        while (this.buffer.length > 0) {
            const bufferItem = this.buffer.shift()

            if (bufferItem) {
                this.displayCallback(bufferItem, false)
            }
        }

        this.displayCallback(null, true) // 通知完成
    }

    // 暂停打字机
    pause(): void {
        this.stopAnimation()
    }

    // 恢复打字机
    resume(): void {
        if (this.buffer.length > 0 && !this.timer) {
            this.startTypewriter()
        }
    }

    // 停止动画
    private stopAnimation(): void {
        if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
        }
    }

    // 销毁打字机
    destroy(): void {
        if (this.destroyed) return

        this.stopAnimation()
        this.buffer = []
        this.destroyed = true
        console.log('打字机已销毁')
    }

    reset(): void {
        this.stopAnimation()
        this.buffer = []
    }
}
